import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Flag, Languages, MoreHorizontal, Pencil, Trash2 } from 'lucide-react';

import { useAuthStore } from '@/stores/AuthState';
import { useTranslation } from '@/utils/i18n';
import { toast } from '@/stores/toast';
import { deleteRecommendation, moderateRecommendation, updateCaption } from '@/api/reco';
import { MY_RECOMMENDATIONS_KEY } from '@/constants/queryKeys';
import { patchCaptionInFeeds } from '@/utils/captionCache';
import { PrimaryButton } from '@/components/Buttons';
import { pickCaption } from '@/utils/commentDisplay';
import type { DiscoverResponse } from '@/types';
import { pullRefreshIgnore } from '@/hooks/usePullToRefresh';

/**
 * The "…" menu on a discover card. Everyone can report offensive content; the
 * creator can also edit the comment or delete the recommendation. Mirrors the
 * legacy moderation / owner actions.
 */
export default function DiscoverCardMenu({
  discovery,
  commentTranslated = false,
  showingOriginal = false,
  onToggleOriginal,
}: {
  discovery: DiscoverResponse;
  /** G-tr — the card currently displays a translation of the comment. */
  commentTranslated?: boolean;
  showingOriginal?: boolean;
  onToggleOriginal?: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const userId = useAuthStore((s) => s.userInfo.id);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);

  // reco-comment-replies — the legend is the comment with no circle, never index 0.
  const comment = pickCaption(discovery.comments);
  const isCreator = userId != null && discovery.sender.id === userId;
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['discover-track'] });

  const report = useMutation({
    mutationFn: () => moderateRecommendation(String(comment?.id)),
    onSuccess: () => {
      toast.success(t('toast.reported'));
      setOpen(false);
    },
    onError: () => toast.error(t('toast.actionFailed')),
  });

  const remove = useMutation({
    mutationFn: () => deleteRecommendation(discovery.documentId),
    onSuccess: () => {
      toast.success(t('toast.recoDeleted'));
      void invalidate();
      setOpen(false);
    },
    onError: () => toast.error(t('toast.actionFailed')),
  });

  // Nothing actionable — no comment to report and not the creator → render no
  // menu at all, rather than a sheet containing only "Annuler".
  if (!comment && !isCreator) return null;

  return (
    <>
      <button
        type="button"
        aria-label="More options"
        onClick={() => setOpen(true)}
        data-testid={`btn-card-menu-${discovery.id}`}
        // z-20: sit above the frosted track card (z-10); without it the button
        // was painted behind the card and taps did nothing / it looked missing.
        className="tw:absolute tw:right-5 tw:top-4 tw:z-20 tw:flex tw:size-10 tw:items-center tw:justify-center tw:rounded-full tw:btn-circle tw:text-white"
      >
        <MoreHorizontal size={18} />
      </button>

      {open && (
        <div
          className="tw:fixed tw:inset-0 tw:z-50 tw:flex tw:items-end tw:bg-black/50"
          onClick={() => setOpen(false)}
          data-testid="card-menu-sheet"
          {...pullRefreshIgnore}
        >
          <div
            className="tw:w-full tw:rounded-t-3xl tw:bg-dark-1 tw:p-4 tw:pb-8"
            style={{ paddingBottom: 'calc(2rem + env(safe-area-inset-bottom))' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* G-tr — flip between the translation and the original comment. */}
            {comment && (commentTranslated || showingOriginal) && (
              <MenuItem
                icon={<Languages size={20} />}
                label={showingOriginal ? t('reco.menu.seeTranslation') : t('reco.menu.seeOriginal')}
                onClick={() => {
                  onToggleOriginal?.();
                  setOpen(false);
                }}
                testid="menu-see-original"
              />
            )}
            {comment && (
              <MenuItem
                icon={<Flag size={20} />}
                label={t('reco.menu.report')}
                onClick={() => report.mutate()}
                testid="menu-report"
              />
            )}
            {isCreator && (
              <>
                <MenuItem
                  icon={<Pencil size={20} />}
                  label={t('reco.menu.edit')}
                  onClick={() => {
                    setOpen(false);
                    setEditing(true);
                  }}
                  testid="menu-edit"
                  disabled={!comment}
                />
                <MenuItem
                  icon={<Trash2 size={20} />}
                  label={t('reco.menu.delete')}
                  onClick={() => remove.mutate()}
                  testid="menu-delete"
                  danger
                />
              </>
            )}
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="tw:mt-2 tw:h-12 tw:w-full tw:rounded-xl tw:bg-dark-primary tw:text-base tw:font-semibold tw:text-white"
            >
              {t('reco.menu.cancel')}
            </button>
          </div>
        </div>
      )}

      {editing && comment && (
        <EditCommentModal
          recoId={discovery.id}
          current={comment.content}
          onClose={() => setEditing(false)}
          onSaved={(legend) => {
            // caption-edit-retranslate — written into the cache, never a feed
            // refetch: invalidating the infinite query reshuffled the deck
            // under the reader's finger.
            patchCaptionInFeeds(queryClient, discovery.id, legend);
            void queryClient.invalidateQueries({ queryKey: MY_RECOMMENDATIONS_KEY });
            setEditing(false);
          }}
        />
      )}
    </>
  );
}

function MenuItem({
  icon,
  label,
  onClick,
  testid,
  danger,
  disabled,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  testid?: string;
  danger?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={
        'tw:flex tw:h-14 tw:w-full tw:items-center tw:gap-3 tw:rounded-xl tw:px-3 tw:text-left tw:text-base tw:disabled:opacity-40 ' +
        (danger ? 'tw:text-error' : 'tw:text-white')
      }
    >
      {icon}
      {label}
    </button>
  );
}

function EditCommentModal({
  recoId,
  current,
  onClose,
  onSaved,
}: {
  recoId: number;
  current: string;
  onClose: () => void;
  onSaved: (legend: Awaited<ReturnType<typeof updateCaption>>) => void;
}) {
  const { t } = useTranslation();
  const [value, setValue] = useState(current);

  const mutation = useMutation({
    mutationFn: () => updateCaption(recoId, value.trim()),
    onSuccess: (legend) => {
      toast.success(t('toast.recoUpdated'));
      onSaved(legend);
    },
    onError: () => toast.error(t('toast.actionFailed')),
  });

  return (
    <div
      className="tw:fixed tw:inset-0 tw:z-[60] tw:flex tw:items-center tw:justify-center tw:bg-black/60 tw:px-6"
      role="dialog"
      aria-modal="true"
      data-testid="modal-edit-comment"
      {...pullRefreshIgnore}
    >
      <div className="tw:w-full tw:max-w-[340px] tw:rounded-2xl tw:bg-dark-1 tw:p-5">
        <h3 className="tw:mb-3 tw:heading-3 tw:text-white">{t('reco.menu.edit')}</h3>
        <textarea
          value={value}
          onChange={(e) => setValue(e.target.value)}
          maxLength={300}
          rows={3}
          data-testid="input-edit-comment"
          className="tw:w-full tw:resize-none tw:rounded-xl tw:border tw:border-blue-2/40 tw:bg-dark-primary tw:px-4 tw:py-3 tw:text-base tw:text-white"
        />
        <div className="tw:mt-4 tw:flex tw:gap-3">
          <button
            type="button"
            onClick={onClose}
            className="tw:h-12 tw:flex-1 tw:rounded-xl tw:bg-dark-primary tw:text-base tw:text-white"
          >
            {t('reco.menu.cancel')}
          </button>
          <div className="tw:flex-1">
            <PrimaryButton
              label={t('profile.save')}
              onClick={() => value.trim() && mutation.mutate()}
              loading={mutation.isPending}
              data-testid="btn-save-comment"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
